import { ReactNode, useState } from 'react';

import { DialogProps } from '@cast/design-system';
import { trackClusterConnected } from '@cast/infrastructure-analytics';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useConsoleAccess, useReportingTrial } from '@cast/shared-trial-ui';
import { Cluster } from '@cast/types';

import { SlideDialog } from 'hooks/dialogs/cluster/_components/SlideDialog';
import { FeatureSelection } from 'hooks/dialogs/cluster/types';
import { useIsReliabilityMetricsAvailableInBeta } from 'hooks/permissions/useIsReliabilityMetricsAvailableInBeta';

import { mapCastctlFeatureSelectionToLegacyFeatureSelection } from './_utils';
import { ConnectingStep } from './ConnectingStep';
import { ErrorStep } from './ErrorStep';
import { getCastctlFeatureSelectionDefaults } from './getCastctlFeatureSelectionDefaults';
import { SetupStep } from './SetupStep';
import { SuccessStep } from './SuccessStep';
import { CastctlFeatureSelection } from './types';

type CastctlDialogStep = 'setup' | 'connecting' | 'error' | 'success';

const WIDE_SLIDES: CastctlDialogStep[] = ['setup'];

export type CastctlOnboardClusterDialogProps = Omit<DialogProps, 'onClose'> & {
  skipConnectionMonitoring?: boolean;
  initialFeatureSelection?: Partial<CastctlFeatureSelection>;
  onClose: () => void;
  onCompleted: () => void;
  onClusterConnected?: (cluster: Cluster) => void;
  onFeaturesSelected?: (features: FeatureSelection) => void;
  onSwitchToLegacyScript?: () => void;
};

export const CastctlOnboardClusterDialog = ({
  skipConnectionMonitoring = false,
  initialFeatureSelection,
  onClose,
  onCompleted,
  onClusterConnected,
  onFeaturesSelected,
  onSwitchToLegacyScript,
  ...dialogProps
}: CastctlOnboardClusterDialogProps) => {
  const { currentOrganization } = useOrganizations();
  const { isLoading: isReportingLoading } = useReportingTrial();
  const { limitAutomationConsole } = useConsoleAccess();
  const isReliabilityMetricsAvailableInBeta =
    useIsReliabilityMetricsAvailableInBeta();

  // Fail open to main-console defaults while reporting status loads; settle once ready.
  const useCostMonitoringOnlyDefaults =
    !isReportingLoading && limitAutomationConsole;

  const [featureOverrides, setFeatureOverrides] = useState<
    Partial<CastctlFeatureSelection>
  >({});
  const requestedFeatureSelection: CastctlFeatureSelection = {
    ...getCastctlFeatureSelectionDefaults(useCostMonitoringOnlyDefaults),
    ...initialFeatureSelection,
    ...featureOverrides,
  };
  const featureSelection: CastctlFeatureSelection =
    isReliabilityMetricsAvailableInBeta
      ? requestedFeatureSelection
      : { ...requestedFeatureSelection, reliabilityMetrics: false };

  const [dialogStep, setDialogStep] = useState<CastctlDialogStep>('setup');
  const [connectedCluster, setConnectedCluster] = useState<
    Cluster | undefined
  >();

  const [clusterScanTimeFrom] = useState<Date>(new Date());

  return (
    <SlideDialog<Record<CastctlDialogStep, ReactNode>>
      testId="castctl-onboard-cluster-dialog"
      wideSlides={WIDE_SLIDES}
      onClose={onClose}
      activeSlide={dialogStep}
      slides={{
        setup: (
          <SetupStep
            featureSelection={featureSelection}
            onFeatureChange={({ feature, enabled }) => {
              if (
                feature === 'reliabilityMetrics' &&
                !isReliabilityMetricsAvailableInBeta
              ) {
                return;
              }

              setFeatureOverrides((previous) => ({
                ...previous,
                [feature]: enabled,
              }));
            }}
            onDone={() => {
              onFeaturesSelected?.(
                mapCastctlFeatureSelectionToLegacyFeatureSelection(
                  featureSelection
                )
              );
              if (skipConnectionMonitoring) {
                onCompleted();
                return;
              }
              setDialogStep('connecting');
            }}
            onClose={onClose}
            onSwitchToLegacyScript={onSwitchToLegacyScript}
          />
        ),
        connecting: (
          <ConnectingStep
            clusterScanTimeFrom={clusterScanTimeFrom}
            onBack={() => {
              setDialogStep('setup');
            }}
            onClose={onClose}
            onClusterConnected={(cluster) => {
              if (currentOrganization?.id) {
                trackClusterConnected({
                  organization_id: currentOrganization.id,
                  cluster_id: cluster.id,
                });
              }
              setConnectedCluster(cluster);
              onClusterConnected?.(cluster);
              setDialogStep('success');
            }}
            onTimeout={() => {
              setDialogStep('error');
            }}
          />
        ),
        error: (
          <ErrorStep
            onClose={onClose}
            onRetry={() => {
              setDialogStep('setup');
            }}
          />
        ),
        success: connectedCluster && (
          <SuccessStep cluster={connectedCluster} onClose={onCompleted} />
        ),
      }}
      {...dialogProps}
    />
  );
};
